// ==============================================================================
// assets/script.size-guide.js
//
// PVR-aware Size Guide replacement for the Printful Size Guide (PSG) Shopify
// App Embed. This file is a direct functional replacement for the Shopify App
// Embed's PrintfulSizeGuide.js — same architectural slot (on-page script that
// wraps the size-guide title in a link and opens a modal with the tables), but
// adds PVR awareness so the chart stays in sync with the selected Style.
//
// See HANDOFF-NOTES.md for:
//   - Transferable insights for adapting into the PSG app upstream
//   - Rollout sequence, troubleshooting, and lifecycle rationale
//
// Key design choice — NO closure caching of DOM nodes. The original PSG app
// bug was caused by caching detached tables in closure variables. We eliminate
// the class of bug entirely: every click re-reads the current variant ID and
// looks up fresh HTML in the ProductData JSON node.
// ==============================================================================

(function() {
  'use strict';

  // ============================================================================
  // CONFIG — one-line flips
  // ============================================================================

  // 'theme-native' uses #quick-modal + MicroModal (requires host theme).
  // 'standalone' builds its own modal; portable to any theme.
  var MODAL_MODE = 'theme-native'; // 'theme-native' | 'standalone'

  // Countries whose locales predominantly use imperial measurement.
  // Edit this list if you need different locale behavior.
  var IMPERIAL_COUNTRIES = ['US', 'LR', 'MM'];

  // User-facing copy. Edit to customize or translate.
  // No runtime backend-config read — by design. See HANDOFF-NOTES.md item 9.
  // Rendered as a centered bulleted list (see .sg-measure-list CSS).
  var HOW_TO_MEASURE_ITEMS = [
    'Measure LENGTH (A) at high point of shoulder to bottom.',
    'Measure WIDTH/CHEST (B) across armpit, from side to side.',
    'Allow for +/- of 1/2 inch tolerance for Length and/or Width.',
    'TIP 1: Measure a similar favorite you already own to choose the right size.',
    'TIP 2: For a Loose Fit, consider a size up due to any shrinkage from washing/drying.'
  ];
  var CARE_TIP_TEXT =
    'TIP: Wash & Dry garment Inside-Out to increase life of garment and design print.';

  // Visible text shown on the Size Guide link. Overrides whatever text PSG
  // stamped into the <strong class="size-guide-title"> tag. Edit to customize.
  var LINK_TEXT = 'Click for Size Guide';

  // ============================================================================
  // CONSTANTS (non-config)
  // ============================================================================
  var PRODUCT_SECTION_SELECTOR = 'section[data-section-type="product_page"]';
  var TITLE_SELECTOR = 'strong.size-guide-title';
  var LINK_ID = 'size-guide-link';
  var DATA_NODE_PREFIX = 'ProductData-';
  var LOG_PREFIX = '[size-guide]';
  var ROUNDED_CLASS = 'sg-modal-rounded';

  // ============================================================================
  // STATE (scoped, minimal)
  // Note: deliberately NO variables for cached tables/imperialDiv/metricDiv.
  // That is the pattern that created the original PSG app bug.
  // ============================================================================
  var rootSection = null;
  var sizeGuideLink = null;
  var sectionData = null;
  var warnedOnce = {};

  // ============================================================================
  // ENTRY
  // ============================================================================
  function init() {
    rootSection = document.querySelector(PRODUCT_SECTION_SELECTOR);
    if (!rootSection) return;

    var strong = rootSection.querySelector(TITLE_SELECTOR);
    if (!strong) return; // non-PVR product without a PSG-stamped size guide

    sectionData = readProductData(rootSection);
    if (!sectionData) {
      warnOnce('ProductData node missing or unparseable — size guide disabled.');
      return;
    }
    if (!sectionData.size_guide_html) return; // Liquid not yet deployed; silent

    sizeGuideLink = wrapAsLink(strong);
    attachClickHandler(sizeGuideLink);
    hideInlineTables(rootSection);
    ensureLinkStylesInjected();
    updateLinkVisibility();

    // Runs on every variant change. Color/Size changes fire too, but the
    // operation is idempotent because the map returns the same HTML for all
    // variants within a Style. Not gating on option type by design — the
    // check is microseconds and the gate would add brittle state.
    document.addEventListener('_changeVariant', updateLinkVisibility);
  }

  // ============================================================================
  // DATA ACCESS
  // ============================================================================
  function readProductData(sectionEl) {
    var id = sectionEl.getAttribute('data-section-id');
    var node = document.getElementById(DATA_NODE_PREFIX + id);
    if (!node) return null;
    try { return JSON.parse(node.textContent); }
    catch (e) { return null; }
  }

  function getCurrentVariantId() {
    var input = rootSection.querySelector('[name="id"]');
    return input ? input.value : null;
  }

  function getCurrentHtml() {
    var id = getCurrentVariantId();
    if (!id || !sectionData.size_guide_html) return null;
    return sectionData.size_guide_html[id] || null;
  }

  // ============================================================================
  // LINK
  // ============================================================================
  function wrapAsLink(strong) {
    // Clear the inline display:none that the PSG app adds to the <strong>
    // (it expects its own script to un-hide it by wrapping it in an <a>).
    strong.style.display = '';
    // Override PSG's original text with our configurable LINK_TEXT.
    strong.textContent = LINK_TEXT;
    var a = document.createElement('a');
    a.id = LINK_ID;
    a.href = '#';
    strong.parentNode.insertBefore(a, strong);
    // Prepend a small ruler icon. CSS in ensureThemeNativeStylesInjected /
    // ensureStandaloneStylesInjected handles sizing and alignment.
    a.appendChild(createRulerIcon());
    a.appendChild(strong);
    return a;
  }

  function createRulerIcon() {
    // Lucide-style ruler. SVG uses stroke=currentColor so it inherits
    // the link's text color (matches whatever theme styling applies).
    var ns = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('class', 'sg-ruler-icon');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    var paths = [
      'M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L2.7 8.7a2.41 2.41 0 0 1 0-3.4l2.6-2.6a2.41 2.41 0 0 1 3.4 0Z',
      'm14.5 12.5 2-2',
      'm11.5 9.5 2-2',
      'm8.5 6.5 2-2',
      'm17.5 15.5 2-2'
    ];
    for (var i = 0; i < paths.length; i++) {
      var p = document.createElementNS(ns, 'path');
      p.setAttribute('d', paths[i]);
      svg.appendChild(p);
    }
    return svg;
  }

  var linkStylesInjected = false;

  function ensureLinkStylesInjected() {
    // Link-related CSS (icon size, flex layout, spacing). Must inject at
    // init time, not lazily on first modal open — otherwise the link
    // appears unstyled between page load and first click.
    if (linkStylesInjected) return;
    linkStylesInjected = true;
    var css =
      '#' + LINK_ID + ' { display: inline-flex; align-items: center; gap: 6px; margin-top: 0.75em; }' +
      '#' + LINK_ID + ' .sg-ruler-icon { width: 1em; height: 1em; flex-shrink: 0; }' +
      // :has() is widely supported in modern browsers (2023+).
      'p:has(> #' + LINK_ID + ') { margin-top: 0.75em; }';
    var style = document.createElement('style');
    style.id = 'sg-link-css';
    style.textContent = css;
    document.head.appendChild(style);
  }

  function hideInlineTables(sectionEl) {
    // PSG stamped the size-guide tables directly into product.description,
    // then removed them from the DOM via its own script. With PSG disabled,
    // those tables would render visibly inline beneath the description.
    // Hide them here — our modal renders fresh copies from size_guide_html.
    // Scoped to the product section so our modal's copies (in #quick-modal,
    // elsewhere in the DOM) are unaffected.
    var inlineTables = sectionEl.querySelectorAll(
      '.table-responsive.dynamic[data-unit-system]'
    );
    for (var i = 0; i < inlineTables.length; i++) {
      inlineTables[i].style.display = 'none';
    }
  }

  function updateLinkVisibility() {
    if (!sizeGuideLink) return;
    sizeGuideLink.style.display = getCurrentHtml() ? '' : 'none';
  }

  function attachClickHandler(link) {
    link.addEventListener('click', function(e) {
      e.preventDefault();
      var html = getCurrentHtml();
      if (!html) return; // defensive — link should be hidden anyway
      openModal(html);
    });
  }

  // ============================================================================
  // LOCALE / UNITS
  // ============================================================================
  function detectUnitSystem() {
    // Tier 1: Shopify storefront's country value (most reliable when set).
    if (typeof Shopify !== 'undefined' && Shopify.country) {
      var cc = String(Shopify.country).toUpperCase();
      return IMPERIAL_COUNTRIES.indexOf(cc) !== -1 ? 'imperial' : 'metric';
    }
    // Tier 2: Browser locale (e.g. "en-US", "en-GB", "fr-FR").
    var lang = (navigator.language || '').toUpperCase();
    var country = lang.split('-')[1];
    if (country && IMPERIAL_COUNTRIES.indexOf(country) !== -1) return 'imperial';
    // Default: metric (majority of world).
    return 'metric';
  }

  function applyUnitVisibility(root, unit) {
    var hideName = unit === 'imperial' ? 'metric' : 'imperial';
    var hideEl = root.querySelector('[data-unit-system="' + hideName + '"]');
    var showEl = root.querySelector('[data-unit-system="' + unit + '"]');
    if (hideEl) hideEl.style.display = 'none';
    if (showEl) showEl.style.display = '';
  }

  // ============================================================================
  // MODAL CONTENT
  // ============================================================================
  function buildContentMarkup(sizeGuideHtml) {
    var assets = sectionData.size_guide_assets || {};
    var imgUrl = assets.how_to_measure_image || '';
    var imgEl = '';
    if (imgUrl) {
      imgEl =
        '<img class="sg-measure-img" src="' + escapeAttr(imgUrl) + '" ' +
        'alt="How to measure garment length (A) and width (B)" ' +
        'onerror="this.style.display=\'none\'">';
    }
    var listItems = '';
    for (var i = 0; i < HOW_TO_MEASURE_ITEMS.length; i++) {
      listItems += '<li>' + escapeHtml(HOW_TO_MEASURE_ITEMS[i]) + '</li>';
    }
    return (
      '<div class="sg-content">' +
        imgEl +
        '<ul class="sg-measure-list">' + listItems + '</ul>' +
        '<div class="sg-tables">' + sizeGuideHtml + '</div>' +
        '<p class="sg-care-tip">' + escapeHtml(CARE_TIP_TEXT) + '</p>' +
      '</div>'
    );
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escapeAttr(s) { return escapeHtml(s); }

  // ============================================================================
  // MODAL DISPATCH
  // ============================================================================
  function openModal(sizeGuideHtml) {
    if (MODAL_MODE === 'theme-native') {
      openThemeNativeModal(sizeGuideHtml);
    } else {
      openStandaloneModal(sizeGuideHtml);
    }
  }

  // ----------------------------------------------------------------------------
  // Theme-native mode: reuse #quick-modal + MicroModal
  // ----------------------------------------------------------------------------
  var themeNativeStylesInjected = false;

  function ensureThemeNativeStylesInjected() {
    if (themeNativeStylesInjected) return;
    themeNativeStylesInjected = true;
    var css =
      '.modal-container.' + ROUNDED_CLASS + ' { border-radius: 12px; overflow: hidden; }' +
      '.sg-content { max-height: 85vh; overflow-y: auto; padding: 4px 4px 8px; }' +
      '.sg-measure-img { max-width: 100%; height: auto; display: block; margin: 0 auto 12px; }' +
      // Bulleted list: block is centered (margin: 0 auto) with a max-width;
      // text inside each <li> stays left-aligned by default.
      // Custom bullet via ::before (theme CSS was killing native list-style
      // disc-outside so wrapped lines weren't hang-indenting). padding-left
      // + negative text-indent on the li forms the hanging indent; the
      // ::before bullet fills the text-indent slot.
      '.sg-measure-list { list-style: none !important; padding: 0 !important; margin: 0 0 14px !important; text-align: left !important; }' +
      '.sg-measure-list li { font-size: 12px !important; line-height: 1.3 !important; color: #444 !important; margin: 0 !important; padding: 0 0 0 1.25em !important; text-indent: -1.25em !important; display: block !important; }' +
      '.sg-measure-list li + li { margin-top: 0 !important; padding-top: 0 !important; }' +
      '.sg-measure-list li::before { content: "\\2022 " !important; display: inline-block !important; width: 1.25em !important; text-indent: 0 !important; vertical-align: baseline !important; line-height: inherit !important; margin: 0 !important; padding: 0 !important; }' +
      '.sg-tables { margin-bottom: 14px; }' +
      '.sg-tables table { width: 100%; border-collapse: collapse; }' +
      '.sg-care-tip { font-size: 12px; line-height: 1.5; margin: 0; color: #666; font-style: italic; }';
    var style = document.createElement('style');
    style.id = 'sg-theme-native-css';
    style.textContent = css;
    document.head.appendChild(style);
  }

  function openThemeNativeModal(sizeGuideHtml) {
    var modal = document.getElementById('quick-modal');
    if (!modal) {
      warnOnce('theme-native mode requires #quick-modal element. Switch MODAL_MODE to "standalone" or add the modal shell.');
      return;
    }
    if (typeof MicroModal === 'undefined') {
      warnOnce('theme-native mode requires global MicroModal. Switch MODAL_MODE to "standalone" or load MicroModal.');
      return;
    }
    var container = modal.querySelector('.modal-container');
    var content = modal.querySelector('.modal-container .modal-content');
    if (!container || !content) {
      warnOnce('theme-native mode: #quick-modal missing expected .modal-container/.modal-content structure.');
      return;
    }
    ensureThemeNativeStylesInjected();
    content.innerHTML = buildContentMarkup(sizeGuideHtml);
    applyUnitVisibility(content, detectUnitSystem());
    container.classList.add(ROUNDED_CLASS);
    MicroModal.show('quick-modal', {
      onShow: function() {
        // Mirror the theme's own Tooltip() pattern: .loaded on BOTH the
        // modal AND the modal-container. The theme's CSS needs both.
        modal.classList.add('loaded');
        container.classList.add('loaded');
        document.body.classList.add('modal-active');
      },
      onClose: function() {
        modal.classList.remove('loaded');
        container.classList.remove('loaded');
        document.body.classList.remove('modal-active');
        container.classList.remove(ROUNDED_CLASS);
        content.innerHTML = '';
      }
    });
  }

  // ----------------------------------------------------------------------------
  // Standalone mode: ships its own modal shell + CSS
  // ----------------------------------------------------------------------------
  var standaloneShellBuilt = false;
  var standaloneStylesInjected = false;
  var standaloneShellEl = null;

  function ensureStandaloneStylesInjected() {
    if (standaloneStylesInjected) return;
    standaloneStylesInjected = true;
    var css = [
      // Note: link styles (icon/spacing) are injected separately at init time
      // via ensureLinkStylesInjected() — they need to apply before any modal
      // opens. This block is strictly for the standalone modal shell.
      '#sg-standalone-shell { display: none; position: fixed; inset: 0; z-index: 10000; }',
      '#sg-standalone-shell.sg-open { display: flex; align-items: center; justify-content: center; }',
      '#sg-standalone-shell .sg-backdrop { position: absolute; inset: 0; background: rgba(0,0,0,0.5); }',
      '#sg-standalone-shell .sg-dialog {',
      '  position: relative; background: #fff; border-radius: 12px;',
      '  max-width: 480px; width: calc(100vw - 32px); max-height: 90vh;',
      '  overflow: hidden; box-shadow: 0 16px 40px rgba(0,0,0,0.22);',
      '  display: flex; flex-direction: column;',
      '}',
      '#sg-standalone-shell .sg-close {',
      '  position: absolute; top: 10px; right: 14px; z-index: 1;',
      '  background: transparent; border: none; font-size: 22px;',
      '  cursor: pointer; color: #333; line-height: 1; padding: 4px 8px;',
      '}',
      '#sg-standalone-shell .sg-body { padding: 28px 24px 20px; overflow-y: auto; }',
      '#sg-standalone-shell .sg-measure-img { max-width: 100%; height: auto; display: block; margin: 0 auto 12px; }',
      '#sg-standalone-shell .sg-measure-list { list-style: none !important; padding: 0 !important; margin: 0 0 14px !important; text-align: left !important; }',
      '#sg-standalone-shell .sg-measure-list li { font-size: 12px !important; line-height: 1.3 !important; color: #444 !important; margin: 0 !important; padding: 0 0 0 1.25em !important; text-indent: -1.25em !important; display: block !important; }',
      '#sg-standalone-shell .sg-measure-list li + li { margin-top: 0 !important; padding-top: 0 !important; }',
      '#sg-standalone-shell .sg-measure-list li::before { content: "\\2022 " !important; display: inline-block !important; width: 1.25em !important; text-indent: 0 !important; vertical-align: baseline !important; line-height: inherit !important; margin: 0 !important; padding: 0 !important; }',
      '#sg-standalone-shell .sg-tables { margin-bottom: 14px; }',
      '#sg-standalone-shell .sg-tables table { width: 100%; border-collapse: collapse; }',
      '#sg-standalone-shell .sg-care-tip { font-size: 12px; line-height: 1.5; margin: 0; color: #666; font-style: italic; }',
      'body.sg-modal-active { overflow: hidden; }'
    ].join('\n');
    var style = document.createElement('style');
    style.id = 'sg-standalone-css';
    style.textContent = css;
    document.head.appendChild(style);
  }

  function ensureStandaloneShellBuilt() {
    if (standaloneShellBuilt) return;
    standaloneShellBuilt = true;
    var shell = document.createElement('div');
    shell.id = 'sg-standalone-shell';
    shell.setAttribute('role', 'dialog');
    shell.setAttribute('aria-modal', 'true');
    shell.setAttribute('aria-label', 'Size Guide');
    shell.innerHTML =
      '<div class="sg-backdrop"></div>' +
      '<div class="sg-dialog">' +
        '<button class="sg-close" aria-label="Close">&times;</button>' +
        '<div class="sg-body"></div>' +
      '</div>';
    document.body.appendChild(shell);
    shell.querySelector('.sg-backdrop').addEventListener('click', closeStandaloneModal);
    shell.querySelector('.sg-close').addEventListener('click', closeStandaloneModal);
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && shell.classList.contains('sg-open')) {
        closeStandaloneModal();
      }
    });
    standaloneShellEl = shell;
  }

  function openStandaloneModal(sizeGuideHtml) {
    ensureStandaloneStylesInjected();
    ensureStandaloneShellBuilt();
    var body = standaloneShellEl.querySelector('.sg-body');
    body.innerHTML = buildContentMarkup(sizeGuideHtml);
    applyUnitVisibility(body, detectUnitSystem());
    standaloneShellEl.classList.add('sg-open');
    document.body.classList.add('sg-modal-active');
    var closeBtn = standaloneShellEl.querySelector('.sg-close');
    if (closeBtn) closeBtn.focus();
  }

  function closeStandaloneModal() {
    if (!standaloneShellEl) return;
    standaloneShellEl.classList.remove('sg-open');
    document.body.classList.remove('sg-modal-active');
    standaloneShellEl.querySelector('.sg-body').innerHTML = '';
  }

  // ============================================================================
  // UTIL
  // ============================================================================
  function warnOnce(msg) {
    if (warnedOnce[msg]) return;
    warnedOnce[msg] = true;
    if (typeof console !== 'undefined' && console.warn) {
      console.warn(LOG_PREFIX, msg);
    }
  }

  // ============================================================================
  // BOOT
  // ============================================================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
